import React, { useEffect, useState, useCallback } from 'react';
import {
  Upload,
  Loader2,
  Trash2,
  Users2,
  CalendarCheck,
  AlertTriangle,
  Lightbulb,
  FileCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const RAG_STYLES = {
  green: { backgroundColor: '#43a047', color: '#fff' },
  amber: { backgroundColor: '#ffb300', color: '#000' },
  red: { backgroundColor: '#ff5252', color: '#fff' },
};

const FLAG_STYLES = {
  good: { backgroundColor: '#43a047', color: '#fff' },
  watch: { backgroundColor: '#ffb300', color: '#000' },
  concern: { backgroundColor: '#ff5252', color: '#fff' },
};

const pct = (v) => `${Math.round(Number(v) || 0)}%`;

const StatBox = ({ label, value }) => (
  <div className="bg-cream border border-ink/10 rounded-lg p-4">
    <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">{label}</div>
    <div className="font-bold tracking-tight text-2xl">{value}</div>
  </div>
);

const CommitteeCard = ({ committee }) => (
  <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h3 className="font-bold tracking-tight text-lg flex items-center gap-2">
        <Users2 size={18} /> {committee.name}
      </h3>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-cream">
          {committee.meetings_held} meeting{committee.meetings_held === 1 ? '' : 's'}
        </span>
        <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-lime">
          Avg {pct(committee.average_attendance_percent)}
        </span>
      </div>
    </div>

    {committee.quorum_concerns && (
      <div className="flex items-start gap-2 border border-ink/10 rounded-lg p-3 mb-4 text-sm font-medium" style={{ backgroundColor: '#fff3e0' }}>
        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
        <span>{committee.quorum_concerns}</span>
      </div>
    )}

    {(committee.members || []).length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-wide text-ink/50 border-b border-ink/10">
              <th className="py-2 pr-3">Member</th>
              <th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">Attended</th>
              <th className="py-2 pr-3">Rate</th>
              <th className="py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {committee.members.map((m, i) => (
              <tr key={i} className="border-b border-ink/5 last:border-0">
                <td className="py-2 pr-3 font-bold">{m.name}</td>
                <td className="py-2 pr-3 font-medium text-ink/60">{m.role || '—'}</td>
                <td className="py-2 pr-3 font-medium">{m.attended}/{m.eligible}</td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-cream border border-ink/10 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: pct(m.attendance_percent),
                          backgroundColor: (FLAG_STYLES[m.flag] || FLAG_STYLES.watch).backgroundColor,
                        }}
                      />
                    </div>
                    <span className="font-bold">{pct(m.attendance_percent)}</span>
                  </div>
                </td>
                <td className="py-2">
                  <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg" style={FLAG_STYLES[m.flag] || FLAG_STYLES.watch}>
                    {m.flag}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {(committee.insights || []).length > 0 && (
      <ul className="mt-4 space-y-1">
        {committee.insights.map((ins, i) => (
          <li key={i} className="flex items-start gap-2 text-sm font-medium text-ink/70">
            <Lightbulb size={14} className="mt-0.5 flex-shrink-0" />
            {ins}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AnalysisResult = ({ doc, onDelete, readOnly }) => {
  const a = doc.analysis || {};
  return (
    <div className="space-y-5">
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <FileCheck size={18} />
            <span className="font-bold break-all">{doc.filename}</span>
            <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg" style={RAG_STYLES[a.rag] || RAG_STYLES.amber}>
              {a.rag || 'amber'}
            </span>
          </div>
          {!readOnly && (
            <button
              onClick={() => onDelete(doc.id)}
              className="p-1.5 bg-red-500 text-white border border-ink/10 rounded-lg flex-shrink-0"
              aria-label="Delete attendance document"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {a.overview && <p className="font-medium text-ink/70 mb-4">{a.overview}</p>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox label="Overall attendance" value={pct(a.overall_attendance_percent)} />
          <StatBox label="Meetings analysed" value={a.total_meetings ?? 0} />
          <StatBox label="Members tracked" value={a.total_members ?? 0} />
          <StatBox label="Committees" value={(a.committees || []).length} />
        </div>
      </div>

      {(a.committees || []).map((c, i) => (
        <CommitteeCard key={i} committee={c} />
      ))}

      {((a.key_findings || []).length > 0 || (a.recommendations || []).length > 0) && (
        <div className="grid md:grid-cols-2 gap-5">
          {(a.key_findings || []).length > 0 && (
            <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
              <h3 className="font-bold tracking-tight text-lg mb-3 flex items-center gap-2">
                <CalendarCheck size={18} /> Key Findings
              </h3>
              <ul className="space-y-2">
                {a.key_findings.map((f, i) => (
                  <li key={i} className="text-sm font-medium text-ink/70 border border-ink/10 rounded-lg bg-cream px-3 py-2">
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(a.recommendations || []).length > 0 && (
            <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
              <h3 className="font-bold tracking-tight text-lg mb-3 flex items-center gap-2">
                <Lightbulb size={18} /> Recommendations
              </h3>
              <ul className="space-y-2">
                {a.recommendations.map((r, i) => (
                  <li key={i} className="text-sm font-medium text-ink/70 border border-ink/10 rounded-lg bg-cream px-3 py-2">
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AttendanceTab = ({ reviewId, readOnly }) => {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.listAttendanceDocuments(reviewId);
      setDocs(data.documents || []);
    } catch (e) {
      setDocs([]);
      toast.error('Could not load attendance analyses');
    }
  }, [reviewId]);

  useEffect(() => {
    if (reviewId) load();
  }, [reviewId, load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadAttendanceDocument(reviewId, file);
      toast.success('Attendance analysed');
      await load();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteAttendanceDocument(id);
      toast.success('Attendance analysis deleted');
      await load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold tracking-tight text-2xl mb-1 flex items-center gap-2">
              <CalendarCheck size={22} /> Committee Attendance
            </h2>
            <p className="font-medium text-ink/70 max-w-2xl">
              Upload minutes, an attendance register or log (PDF, DOCX, TXT, CSV or XLSX).
              The tool analyses attendance for each committee with deep-dive statistics —
              per-member rates, quorum risks, findings and recommendations.
            </p>
          </div>
          {!readOnly && (
            <label className="inline-flex items-center px-6 py-3 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide cursor-pointer">
              {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
              {uploading ? 'Analysing...' : 'Upload document'}
              <input
                type="file"
                className="hidden"
                accept=".pdf,.docx,.txt,.csv,.xlsx"
                disabled={uploading}
                onChange={(e) => {
                  handleUpload(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
        {uploading && (
          <p className="mt-3 text-sm font-bold uppercase tracking-wide text-ink/50">
            Analysing attendance — this can take up to a minute...
          </p>
        )}
      </div>

      {docs === null && (
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center">
          <Loader2 className="animate-spin mx-auto" size={24} />
        </div>
      )}

      {docs && docs.length === 0 && (
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center">
          <Users2 size={36} className="mx-auto mb-3" />
          <h3 className="font-bold tracking-tight text-xl mb-2">No attendance analysed yet</h3>
          <p className="font-medium text-ink/70 max-w-md mx-auto">
            Upload a document containing meeting attendance to see per-committee statistics here.
          </p>
        </div>
      )}

      {(docs || []).map((doc) => (
        <AnalysisResult key={doc.id} doc={doc} onDelete={handleDelete} readOnly={readOnly} />
      ))}
    </div>
  );
};

export default AttendanceTab;
